import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import { AgFinancingInput, FieldMonitoringStatus, Phase2, AgFinancingPhase2Status } from '@indigo-ag/schema';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { useMutation } from '@apollo/client';

import { SingleRow } from '../../shared/SingleRow';
import { PhaseSection, FieldMonitoringUtil, WithConditionalTooltip, tooltipHelperForDisabledElements } from 'shared';
import { StartAnalysisModal } from '../../phases/phase2/StartAnalysisModal/StartAnalysisModal';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { StartAnalysisPhase2Disclaimer } from '../../shared/StartAnalysisPhase2Disclaimer/StartAnalysisPhase2Disclaimer';
import { UserContext } from 'core/components/GateWay/GateWay';
import { TerraMagnaAnalysisState } from '../../shared/credit-analysis/TerramagnaAnalysisState/TerramagnaAnalysisState';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { ToggleOnHoldFieldMonitoringAnalysis } from '../credit-analysis';

interface FieldMonitoringProps {
  fieldMonitoringInfo: Phase2['fieldMonitoring'];
}

export const FieldMonitoringSection: React.FunctionComponent<FieldMonitoringProps> = ({ fieldMonitoringInfo }) => {
  /**
   * State
   */
  const [openStartAnalysis, setOpenStartAnalysis] = useState(false);
  const [isRestartingAnalysis, setIsRestartingAnalysis] = useState(false);

  /**
   * Finance Process Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  /**
   * User Context
   */
  const { isSuper, isCreditAnalyst } = useContext(UserContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const handleCloseStartAnalysis = () => {
    setOpenStartAnalysis(false);
  };

  const { addFlashMessage } = useFlashMessage();

  const restartFieldMonitoring = () => {
    setIsRestartingAnalysis(true);

    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinance?.id,
      phase2: {
        status:
          agFinance?.phase2?.status === AgFinancingPhase2Status.DENIED ||
          agFinance?.phase2?.status === AgFinancingPhase2Status.PENDING_RESTART
            ? AgFinancingPhase2Status.IN_PROGRESS
            : undefined,
        fieldMonitoring: {
          status: FieldMonitoringStatus.IN_PROGRESS
        }
      }
    };

    upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    })
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('fieldMonitoring')} ${i18next.t('restarted')}`,
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsRestartingAnalysis(false);
      });
  };

  const isOnHold = agFinance?.phase2?.status === AgFinancingPhase2Status.ON_HOLD;
  const canStartFieldMonitoring = FieldMonitoringUtil.canStart(
    agFinance?.canStartPhase2,
    agFinance?.collateralType,
    agFinance?.phase2
  );
  const hasProcessStarted = FieldMonitoringUtil.hasStarted(fieldMonitoringInfo);
  const canViewStartButton =
    agFinance?.phase2?.status !== AgFinancingPhase2Status.AWAITING_COMMERCIAL_DEAL && (isSuper || isCreditAnalyst);

  const renderStartButton = () => {
    return !hasProcessStarted && canViewStartButton ? (
      <>
        <WithConditionalTooltip
          visible={!canStartFieldMonitoring}
          title={<StartAnalysisPhase2Disclaimer />}
          placement="top"
        >
          <Button
            variant="primary"
            size="small"
            onClick={() => setOpenStartAnalysis(true)}
            disabled={(!isSuper && !canStartFieldMonitoring) || isOnHold}
            style={tooltipHelperForDisabledElements(!isSuper && !canStartFieldMonitoring)}
            className="text-transform-capitalize"
          >
            {i18next.t('start')}
          </Button>
        </WithConditionalTooltip>
        <StartAnalysisModal
          open={openStartAnalysis}
          startExternalProcess={{ flc: false, luchesi: false, terramagna: true }}
          closeModal={handleCloseStartAnalysis}
        />
      </>
    ) : (
      undefined
    );
  };

  const renderReopenButton = () => {
    return FieldMonitoringUtil.canRestart(agFinance?.phase2) ? (
      <Button
        variant="primary"
        size="small"
        className="text-transform-capitalize"
        onClick={() => restartFieldMonitoring()}
        busy={isRestartingAnalysis}
      >
        {i18next.t('reopen')}
      </Button>
    ) : (
      undefined
    );
  };

  return (
    <PhaseSection
      title={i18next.t('fieldMonitoring')}
      topRightContent={
        <>
          {renderStartButton()}
          {renderReopenButton()}
          {<ToggleOnHoldFieldMonitoringAnalysis />}
        </>
      }
    >
      {hasProcessStarted ? (
        <TerraMagnaAnalysisState fieldMonitoringInfo={fieldMonitoringInfo} />
      ) : (
        <SingleRow title={i18next.t('status')} content={i18next.t('notStarted')} />
      )}
    </PhaseSection>
  );
};
